import { useState } from 'react'
import useActivityLog from '../hooks/useActivityLog'
import Loader from '../components/Loader'
import Button from '../components/Button'
import { format } from 'date-fns'

const formatDetails = (details) => {
  if (!details || typeof details !== 'object') return ''
  return Object.entries(details)
    .map(([k, v]) => `${k}: ${v}`)
    .join(', ')
}

const PAGE_SIZE = 25

const ActivityLogPage = () => {
  const { logs, loading } = useActivityLog()
  const [filter, setFilter] = useState('')
  const [page, setPage] = useState(1)

  const filtered = filter
    ? logs.filter((l) =>
      l.action.toLowerCase().includes(filter.toLowerCase()) ||
      l.user.toLowerCase().includes(filter.toLowerCase()) ||
      formatDetails(l.details).toLowerCase().includes(filter.toLowerCase())
    )
    : logs

  // Reset to the first page whenever the filter changes so results aren't hidden.
  const onFilterChange = (value) => {
    setFilter(value)
    setPage(1)
  }

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const currentPage = Math.min(page, totalPages)
  const startIdx = (currentPage - 1) * PAGE_SIZE
  const paged = filtered.slice(startIdx, startIdx + PAGE_SIZE)

  const exportCsv = () => {
    const escCsv = (val) => {
      const str = String(val ?? '')
      return str.includes(',') || str.includes('"') || str.includes('\n')
        ? `"${str.replace(/"/g, '""')}"`
        : str
    }
    const header = 'Timestamp,User,Action,Details'
    const rows = filtered.map((l) =>
      [
        escCsv(format(new Date(l.timestamp), 'yyyy-MM-dd HH:mm:ss')),
        escCsv(l.user),
        escCsv(l.action),
        escCsv(formatDetails(l.details))
      ].join(',')
    )
    const csv = [header, ...rows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `activity-log-${format(new Date(), 'yyyy-MM-dd')}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  if (loading) {
    return (
      <div className='p-4 flex justify-center py-16'>
        <Loader />
      </div>
    )
  }

  return (
    <div className='p-4'>
      <div className='flex items-center justify-between mb-4'>
        <h1 className='text-4xl'>Activity Log</h1>
        <Button onClick={exportCsv}>Export CSV</Button>
      </div>
      <div className='mb-4'>
        <input
          type='text'
          placeholder='Filter by user, action, or details...'
          className='border rounded p-2 w-full max-w-md text-sm'
          value={filter}
          onChange={(e) => onFilterChange(e.target.value)}
        />
      </div>
      <div className='overflow-x-auto'>
        <table className='w-full text-left text-sm'>
          <thead>
            <tr className='border-b'>
              <th className='p-2'>Timestamp</th>
              <th className='p-2'>User</th>
              <th className='p-2'>Action</th>
              <th className='p-2'>Details</th>
            </tr>
          </thead>
          <tbody>
            {paged.map((l) => (
              <tr key={l.id} className='border-b hover:bg-gray-50'>
                <td className='p-2 whitespace-nowrap'>{format(new Date(l.timestamp), 'yyyy-MM-dd HH:mm:ss')}</td>
                <td className='p-2'>{l.user}</td>
                <td className='p-2'>{l.action}</td>
                <td className='p-2 text-gray-600'>{formatDetails(l.details)}</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={4} className='p-4 text-center text-gray-500'>No activity logged yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {filtered.length > 0 && (
        <div className='mt-4 flex items-center justify-between text-sm text-gray-600'>
          <span>
            Showing {startIdx + 1}–{Math.min(startIdx + PAGE_SIZE, filtered.length)} of {filtered.length}
          </span>
          <div className='flex items-center gap-2'>
            <button
              type='button'
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              className='rounded border border-gray-300 px-3 py-1 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'
            >
              Previous
            </button>
            <span>Page {currentPage} of {totalPages}</span>
            <button
              type='button'
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              className='rounded border border-gray-300 px-3 py-1 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default ActivityLogPage
